import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {SortableItem} from './SortableItem';

const SortableLanguages = ({ allLanguages, initialSelected, name }) => {
  const containerRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setSelected(initialSelected?.slice() ?? []);
  }, [initialSelected]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allLangs = Object.keys(allLanguages);
  const available = allLangs.filter(lang => !selected.includes(lang));
  const filtered = available.filter(lang =>
    allLanguages[lang].toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSelected((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addLanguage = lang => {
    setSelected(prev => [...prev, lang]);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = e => {
    e.preventDefault();
    switch (e.key) {
      case 'ArrowDown':
        setHighlightedIndex(prev => (prev + 1) % filtered.length);
        break;
      case 'ArrowUp':
        setHighlightedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
        break;
      case 'Enter':
        if (highlightedIndex < 0 || filtered.length === 0) {
          return;
        }
        addLanguage(filtered[highlightedIndex]);
        break;
      default:
        if(highlightedIndex < 0 && filtered.length > 0) {
          setHighlightedIndex(0);
        }
        break;
    }
  };

  const removeLanguage = lang => {
    setSelected(prev => prev.filter(c => c !== lang));
  };

  const onMouseEnterItem = idx => () => setHighlightedIndex(idx);
  const onMouseLeaveItem = () => setHighlightedIndex(-1);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div ref={containerRef} className="sortable-languages-container">
        <div className="language-input-wrapper">
          <div className="language-input" onClick={e => {
              e.stopPropagation();
              setShowDropdown(true);
            }}
          >
            {selected.length > 0 && (
              <SortableContext items={selected} strategy={rectSortingStrategy}>
                {selected.map(lang => (
                  <React.Fragment key={lang}>
                    <SortableItem key={lang} id={lang}>
                      <span className="selected-tag" onClick={e => e.stopPropagation()}>
                        {allLanguages[lang]}
                        <span className="remove-tag" onClick={e => {
                            e.stopPropagation();
                            removeLanguage(lang);
                          }}
                          onPointerDown={e => e.stopPropagation()}
                        >✕</span>
                      </span>
                    </SortableItem>
                    <input type="hidden" name={name} value={lang}/>
                  </React.Fragment>
                ))}
              </SortableContext>
            )}

            <input className="search-input" type="text" value={searchTerm} onChange={e => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {showDropdown && (
            <ul className="language-dropdown">
              {filtered.length > 0 ? (
                filtered.map((lang, idx) => (
                  <li key={lang} className={highlightedIndex === idx
                      ? 'dropdown-item highlighted'
                      : 'dropdown-item'
                    }
                    onClick={() => addLanguage(lang)}
                    onMouseEnter={onMouseEnterItem(idx)}
                    onMouseLeave={onMouseLeaveItem}
                  >
                    {allLanguages[lang]}
                  </li>
                ))
              ) : (
                <li className="no-matches">{I18n.t('no_matches')}</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </DndContext>
  );
};
export default SortableLanguages;